import type { IOrder } from './IOrder.js'
import type { FileForProcessing } from '../Sample/ISample.js'
import type { Taxas } from '../ITaxa.js'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from '../../Generated/SampleConfigurationType.js'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.js'
import { SequencingPlatforms } from '../../Generated/SequencingPlatform.js'
import { describeInputShape, getEffectiveSequencingPlatform } from '../../Util/sequencingPlatform.js'

/**
 * What the pipeline did to an order, step by step, so the summary report never branches on an entry
 * point's name. Parameters mirror airflow-dags (`tasks/quality_filtering.py`, `tasks/trimming.py`,
 * `tasks/otu_table_generation.py`, `tasks/amplicon_sorter_params.py`) until the pipeline records them
 * per run; pipeline overrides are not visible here, so an order with overrides is described with the defaults.
 */

export interface Range {
  min: number
  max: number
}

export type ClusteringMethod = 'swarm' | 'vsearch'

export type ProfileCaveat =
  /** neither a detected nor an overridden platform on the order: described as Illumina */
  | 'PLATFORM_ASSUMED'
  /** the run did not record `clustering_method_used`: method inferred from amplicon and platform */
  | 'CLUSTERING_INFERRED'

export interface ProcessingProfile {
  platform: SequencingPlatformKey
  platformOverridden: boolean
  /** reads arrive as R1/R2 pairs and were merged: platform is paired-end and the files are not pre-merged */
  pairedEnd: boolean
  demultiplexed: boolean
  mergeApplied: boolean
  primerRemovalApplied: boolean
  primerRemoval: { errorRate: number; minOverlap: number | null }
  qualityFilter: {
    /** across the order's samples; null when the order has no samples */
    minlen: Range | null
    /** null when any sample has no upper bound (non-CO1 Illumina amplicons filter by minimum length only) */
    maxlen: Range | null
    maxee: number | null
    maxns: number | null
  }
  consensusApplied: boolean
  singletonFilter: boolean
  clusteringMethod: ClusteringMethod
  caveats: ProfileCaveat[]
}

const DEMULTIPLEXED: Record<SampleConfigurationTypeKey, boolean> = {
  SINGLE_SAMPLE_PAIRS: false,
  COMBINED_SAMPLE_PAIRS: true,
  MERGED_AND_TRIMMED_SAMPLES: false,
  MERGED_SAMPLES: false,
  TRIMMED_SAMPLES: false
}

interface PlatformRules {
  primerErrorRate: number
  primerMinOverlap: number | null
  maxee: number | null
  maxns: number | null
  consensusCalling: boolean
  singletonFilter: boolean
  /** length window when the sample sets none; null defers to the amplicon's default */
  lengthFallback: Range | null
  swarmEligible: boolean
}

const PLATFORM_RULES: Record<SequencingPlatformKey, PlatformRules> = {
  ILLUMINA: {
    primerErrorRate: 0,
    primerMinOverlap: null,
    maxee: 1,
    maxns: 0,
    consensusCalling: false,
    singletonFilter: true,
    lengthFallback: null,
    swarmEligible: true
  },
  ONT: {
    primerErrorRate: 0.2,
    primerMinOverlap: 10,
    maxee: null,
    maxns: null,
    consensusCalling: true,
    singletonFilter: false,
    lengthFallback: { min: 100, max: 600 },
    swarmEligible: false
  }
}

interface TaxonRules {
  defaultMinlen: number
  defaultMaxlen: number | null
  swarm: boolean
}

const TAXON_RULES: Record<Taxas, TaxonRules> = {
  CO1_ANIMAL: { defaultMinlen: 200, defaultMaxlen: 315, swarm: true },
  _16S_BACTERIA: { defaultMinlen: 150, defaultMaxlen: null, swarm: false },
  '16S_VERTEBRATES': { defaultMinlen: 150, defaultMaxlen: null, swarm: false },
  FITS2_FUNGAL: { defaultMinlen: 150, defaultMaxlen: null, swarm: false },
  PITS2_PLANT: { defaultMinlen: 150, defaultMaxlen: null, swarm: false },
  '12S_FISH': { defaultMinlen: 95, defaultMaxlen: null, swarm: false },
  OTHER: { defaultMinlen: 150, defaultMaxlen: null, swarm: false },
  custom_primer: { defaultMinlen: 150, defaultMaxlen: null, swarm: false }
}

/** SWARM is used on CO1 only while the run stays under this many sequences; above it the pipeline falls back to vsearch. */
export const SWARM_SEQUENCE_THRESHOLD = 1_500_000

export const CONSENSUS_MAX_READS = 20_000

export type ProfileOrder = Pick<
  IOrder,
  'sampleConfiguration' | 'targetTaxonomy' | 'detectedSequencingPlatform' | 'sequencingPlatformOverride'
>
export type ProfileSample = Pick<FileForProcessing, 'minBasePairLength' | 'maxBasePairLength'>

const range = (values: number[]): Range | null => (values.length === 0 ? null : { min: Math.min(...values), max: Math.max(...values) })

const parseClusteringFlag = (flags?: Record<string, string> | null): ClusteringMethod | null => {
  const value = flags?.clustering_method_used
  return value === 'swarm' || value === 'vsearch' ? value : null
}

/** An order without a detected or overridden platform (submitted before detection existed) is treated as Illumina. */
export const effectivePlatformOrIllumina = (
  order: Pick<IOrder, 'detectedSequencingPlatform' | 'sequencingPlatformOverride'>
): SequencingPlatformKey => getEffectiveSequencingPlatform(order.detectedSequencingPlatform, order.sequencingPlatformOverride) ?? 'ILLUMINA'

/** Reads arrive as R1/R2 pairs: a paired-end platform and files that are not pre-merged. */
export const isPairedEndInput = (
  order: Pick<IOrder, 'sampleConfiguration' | 'detectedSequencingPlatform' | 'sequencingPlatformOverride'>
): boolean =>
  SequencingPlatforms[effectivePlatformOrIllumina(order)].isPairedEnd && !SampleConfigurationTypes[order.sampleConfiguration].impliesMerged

export const deriveProcessingProfile = (
  order: ProfileOrder,
  samples: readonly ProfileSample[],
  flags?: Record<string, string> | null
): ProcessingProfile => {
  const caveats: ProfileCaveat[] = []

  if (getEffectiveSequencingPlatform(order.detectedSequencingPlatform, order.sequencingPlatformOverride) == null) {
    caveats.push('PLATFORM_ASSUMED')
  }
  const platform = effectivePlatformOrIllumina(order)
  const rules = PLATFORM_RULES[platform]
  const configuration = SampleConfigurationTypes[order.sampleConfiguration]
  const taxon = TAXON_RULES[order.targetTaxonomy]

  const pairedEnd = isPairedEndInput(order)

  const minlen = range(samples.map((s) => s.minBasePairLength ?? rules.lengthFallback?.min ?? taxon.defaultMinlen))
  const upperBounds = samples.map((s) => s.maxBasePairLength ?? rules.lengthFallback?.max ?? taxon.defaultMaxlen)
  const maxlen = upperBounds.some((bound) => bound == null) ? null : range(upperBounds as number[])

  let clusteringMethod = parseClusteringFlag(flags)
  if (clusteringMethod == null) {
    caveats.push('CLUSTERING_INFERRED')
    clusteringMethod = rules.swarmEligible && taxon.swarm ? 'swarm' : 'vsearch'
  }

  return {
    platform,
    platformOverridden: order.sequencingPlatformOverride != null,
    pairedEnd,
    demultiplexed: DEMULTIPLEXED[order.sampleConfiguration],
    mergeApplied: pairedEnd,
    primerRemovalApplied: !configuration.impliesTrimmed,
    primerRemoval: { errorRate: rules.primerErrorRate, minOverlap: rules.primerMinOverlap },
    qualityFilter: { minlen, maxlen, maxee: rules.maxee, maxns: rules.maxns },
    consensusApplied: rules.consensusCalling,
    singletonFilter: rules.singletonFilter,
    clusteringMethod,
    caveats
  }
}

/** The cover's explanation of the input, from what the profile says was done. */
export const describeInput = (
  profile: Pick<ProcessingProfile, 'platform' | 'pairedEnd' | 'demultiplexed' | 'primerRemovalApplied'>
): string =>
  describeInputShape({
    platform: profile.platform,
    pairedEnd: profile.pairedEnd,
    multiplexed: profile.demultiplexed,
    primersPresent: profile.primerRemovalApplied
  })

/** Every (configuration, platform) pair the backend accepts; the matrix the report tests enumerate. */
export const supportedProcessingCells = (): { sampleConfiguration: SampleConfigurationTypeKey; platform: SequencingPlatformKey }[] =>
  (Object.keys(SequencingPlatforms) as SequencingPlatformKey[]).flatMap((platform) => {
    const allowed = SequencingPlatforms[platform].allowedSampleConfigurations
    return (Object.keys(SampleConfigurationTypes) as SampleConfigurationTypeKey[])
      .filter((sampleConfiguration) => allowed == null || allowed.includes(sampleConfiguration))
      .map((sampleConfiguration) => ({ sampleConfiguration, platform }))
  })
